import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './CreateCreativeModal.css';

const DEFAULT_AD_TYPES = {
  self_promotion: false,
  social_ad_quota: false,
  social_ad: false,
  cobranding: false
};

const CreateCreativeModal = ({ isOpen, onClose, onCreated }) => {
  const [contracts, setContracts] = useState([]);
  const [kktuList, setKktuList] = useState([]);
  const [creativeTypes, setCreativeTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kktuSearch, setKktuSearch] = useState('');
  const [kktuResults, setKktuResults] = useState([]);
  const [showKktuDropdown, setShowKktuDropdown] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    ad_types: { ...DEFAULT_AD_TYPES }, // Вид рекламы
    brand: '',
    category: '',
    description: '',
    kktu_code: '', // Только один код ККТУ
    targeting: '',
    form: '', // Тип креатива
    target_urls: [''],
    media_files: [],
    texts: [''],
    contract_external_id: ''
  });

  useEffect(() => {
    if (isOpen) {
      // Сбрасываем форму при открытии
      setFormData({
        name: '',
        ad_types: { ...DEFAULT_AD_TYPES },
        brand: '',
        category: '',
        description: '',
        kktu_code: '', // Только один код ККТУ
        targeting: '',
        form: '',
        target_urls: [''],
        media_files: [],
        texts: [''],
        contract_external_id: ''
      });
      loadContracts();
      loadKKTU();
      loadCreativeTypes();
    }
  }, [isOpen]);

  const loadContracts = async () => {
    try {
      const res = await api.get('/contracts/?limit=1000');
      setContracts(res.data);
    } catch (error) {
      console.error('Ошибка загрузки договоров:', error);
    }
  };

  const loadKKTU = async () => {
    try {
      const res = await api.get('/dictionaries/kktu?limit=1000');
      setKktuList(res.data.items || []);
    } catch (error) {
      console.error('Ошибка загрузки ККТУ:', error);
    }
  };

  const searchKKTU = async (query) => {
    if (!query || query.length < 2) {
      setKktuResults([]);
      setShowKktuDropdown(false);
      return;
    }
    
    try {
      const res = await api.get(`/dictionaries/kktu?limit=50&search=${encodeURIComponent(query)}`);
      setKktuResults(res.data.items || []);
      setShowKktuDropdown(true);
    } catch (error) {
      console.error('Ошибка поиска ККТУ:', error);
      setKktuResults([]);
    }
  };

  const handleKktuSearchChange = (e) => {
    const value = e.target.value;
    // Если код уже выбран и пользователь начинает вводить, очищаем выбор
    if (formData.kktu_code && value) {
      setFormData(prev => ({ ...prev, kktu_code: '' }));
    }
    setKktuSearch(value);
    if (value) {
      searchKKTU(value);
    } else {
      setShowKktuDropdown(false);
    }
  };

  const handleKktuSelect = (kktu) => {
    // Заменяем предыдущий код на новый (только один код)
    setFormData(prev => ({
      ...prev,
      kktu_code: kktu.code
    }));
    setKktuSearch('');
    setShowKktuDropdown(false);
  };

  const handleKktuRemove = () => {
    // Очищаем выбранный код и поле поиска
    setFormData(prev => ({
      ...prev,
      kktu_code: ''
    }));
    setKktuSearch('');
    setShowKktuDropdown(false);
  };

  const handleKktuInputFocus = () => {
    // При фокусе на поле, если код уже выбран, начинаем новый поиск
    if (formData.kktu_code) {
      setFormData(prev => ({ ...prev, kktu_code: '' }));
      setKktuSearch('');
    }
  };

  const loadCreativeTypes = async () => {
    try {
      const res = await api.get('/dictionaries/creative-types');
      setCreativeTypes(res.data.items || []);
    } catch (error) {
      console.error('Ошибка загрузки типов креативов:', error);
    }
  };

  const handleAdTypeChange = (type) => {
    setFormData(prev => {
      const current = prev.ad_types || { ...DEFAULT_AD_TYPES };
      const newAdTypes = {
        ...current,
        [type]: !current[type]
      };
      
      // Если включаем саморекламу - очищаем выбранный договор
      const updates = {
        ...prev,
        ad_types: newAdTypes
      };
      
      if (type === 'self_promotion' && newAdTypes.self_promotion) {
        updates.contract_external_id = '';
      }
      
      return updates;
    });
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setLoading(true);
    
    try {
      const uploadedFileIds = [];
      let successCount = 0;
      let failCount = 0;
      
      // Загружаем файлы последовательно с задержкой (rate limit VK ORD)
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          const response = await api.post('/media/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          const data = response.data;
          
          if (data.success && data.file_id) {
            uploadedFileIds.push(data.file_id);
            successCount++;
            
          } else {
            throw new Error('Не удалось получить ID файла');
          }
          
          // Задержка 1 секунда между файлами (кроме последнего)
          if (i < files.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        } catch (error) {
          console.error(`❌ Ошибка загрузки файла ${file.name}:`, error);
          failCount++;
        }
      }
      
      // Добавляем все загруженные file_id в список медиафайлов
      if (uploadedFileIds.length > 0) {
        setFormData(prev => {
          const updatedFiles = [...prev.media_files, ...uploadedFileIds];
          
          return {
            ...prev,
            media_files: updatedFiles
          };
        });
      }
      
      // Показываем результат
      if (successCount > 0 && failCount === 0) {
        alert(`✅ Успешно загружено файлов: ${successCount}`);
      } else if (successCount > 0 && failCount > 0) {
        alert(`⚠️ Загружено: ${successCount} файлов\n❌ Ошибок: ${failCount}`);
      } else {
        alert(`❌ Не удалось загрузить ни один файл`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(`❌ Ошибка загрузки: ${error.message}`);
    } finally {
      setLoading(false);
      // Очищаем input для возможности повторной загрузки
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Валидация обязательных полей
      if (!formData.name || !formData.name.trim()) {
        alert('⚠️ Поле "Название" обязательно для заполнения!\n\nЗаполните поле "Название" и попробуйте снова.');
        setLoading(false);
        return;
      }

      if (!formData.brand || !formData.brand.trim()) {
        alert('⚠️ Поле "Бренд" обязательно для заполнения!\n\nЗаполните поле "Бренд" и попробуйте снова.');
        setLoading(false);
        return;
      }

      if (!formData.kktu_code || !formData.kktu_code.trim()) {
        alert('⚠️ Необходимо выбрать "Код ККТУ"!\n\nВыберите код ККТУ и попробуйте снова.');
        setLoading(false);
        return;
      }

      // Валидация договора - обязателен только если НЕ самореклама
      const isSelfPromotion = formData.ad_types?.self_promotion;
      if (!isSelfPromotion && !formData.contract_external_id) {
        alert('⚠️ Необходимо выбрать "Изначальный договор"!\n\nВыберите договор и попробуйте снова.');
        setLoading(false);
        return;
      }

      const payload = {
        name: formData.name,
        form: formData.form,
        // При саморекламе договор не нужен
        contract_external_id: isSelfPromotion ? null : formData.contract_external_id,
        brand: formData.brand,
        category: formData.category,
        description: formData.description,
        targeting: formData.targeting,
        texts: formData.texts.filter(t => t.trim()),
        target_urls: formData.target_urls.filter(u => u.trim()),
        kktus: formData.kktu_code ? [formData.kktu_code] : [], // Отправляем массив с одним кодом
        media_external_ids: formData.media_files,
        ad_types: formData.ad_types || { ...DEFAULT_AD_TYPES }
      };

      
      

      // Проверка перед отправкой
      if (formData.form === 'text_graphic_block' && (!formData.media_files || formData.media_files.length === 0)) {
        alert('⚠️ Для типа "Текстово-графический блок" необходимо загрузить медиафайл!\n\nЗагрузите изображение и попробуйте снова.');
        setLoading(false);
        return;
      }

      const response = await api.post('/creatives/', payload);

      const result = response.data;
      alert(`✅ Креатив создан!\nERID: ${result.erid}`);
      if (onCreated) onCreated();
      onClose();
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✏️ Создание креатива</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="info-banner">
            <span className="info-icon">ℹ️</span>
            <span>Токен маркировки: появится после сохранения креатива</span>
          </div>

          {/* Название */}
          <div className="form-group">
            <label>Название <span className="required">*</span></label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="Введите название"
              required
            />
          </div>

          {/* Вид рекламы */}
          <div className="form-group">
            <label>Вид рекламы</label>
            <div className="checkbox-group-horizontal">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(formData.ad_types?.self_promotion)}
                  onChange={() => handleAdTypeChange('self_promotion')}
                />
                <span>Самореклама</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(formData.ad_types?.social_ad_quota)}
                  onChange={() => handleAdTypeChange('social_ad_quota')}
                />
                <span>Социальная реклама по квоте</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(formData.ad_types?.social_ad)}
                  onChange={() => handleAdTypeChange('social_ad')}
                />
                <span>Социальная реклама</span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(formData.ad_types?.cobranding)}
                  onChange={() => handleAdTypeChange('cobranding')}
                />
                <span>Кобрендинг</span>
              </label>
            </div>
          </div>

          <div className="info-banner info-light">
            <span className="info-icon">💡</span>
            <span>Подавайте статистику за каждый месяц трансляции рекламы. Например, если реклама показывалась с 1 по 30 июня, подать нужно с 1 по 30 июля.</span>
          </div>

          {/* Бренд */}
          <div className="form-group">
            <label>Бренд <span className="required">*</span></label>
            <input
              type="text"
              className="form-control"
              value={formData.brand}
              onChange={(e) => setFormData({...formData, brand: e.target.value})}
              placeholder='Например, «ООО "ВК"»'
              required
            />
          </div>

          {/* Категория */}
          <div className="form-group">
            <label>Категория</label>
            <input
              type="text"
              className="form-control"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              placeholder="Например, «Умная колонка»"
            />
          </div>

          {/* Описание */}
          <div className="form-group">
            <label>Описание</label>
            <input type="text"
              className="form-control"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Например, «Вышла умная колонка в новом цвете»"
            />
          </div>

          {/* Поиск по кодам ККТУ */}
          <div className="form-group">
            <label>Код ККТУ *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-control"
                value={formData.kktu_code 
                  ? (() => {
                      const kktu = kktuList.find(k => k.code === formData.kktu_code);
                      return kktu ? `${kktu.code} - ${kktu.name}` : formData.kktu_code;
                    })()
                  : kktuSearch}
                onChange={handleKktuSearchChange}
                onFocus={handleKktuInputFocus}
                placeholder="Введите код или название ККТУ"
                readOnly={!!formData.kktu_code}
                style={{ 
                  paddingRight: formData.kktu_code ? '35px' : '10px',
                  cursor: formData.kktu_code ? 'pointer' : 'text'
                }}
              />
              {formData.kktu_code && (
                <button
                  type="button"
                  onClick={handleKktuRemove}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#ef4444',
                    fontSize: '18px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '24px'
                  }}
                  title="Очистить выбранный код"
                >
                  ✕
                </button>
              )}
              {showKktuDropdown && kktuResults.length > 0 && !formData.kktu_code && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                  {kktuResults.map((kktu, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleKktuSelect(kktu)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: idx < kktuResults.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                    >
                      <div style={{ fontWeight: '500', color: '#1f2937' }}>{kktu.code}</div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>{kktu.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Целевая аудитория */}
          <div className="form-group">
            <label>Целевая аудитория</label>
            <input type="text"
              className="form-control"
              value={formData.targeting}
              onChange={(e) => setFormData({...formData, targeting: e.target.value})}
              placeholder="Опишите целевую аудиторию"
              rows={2}
            />
          </div>

          {/* Тип креатива */}
          <div className="form-group">
            <label>Тип креатива <span className="required">*</span></label>
            <select
              className="form-control"
              value={formData.form}
              onChange={(e) => setFormData({...formData, form: e.target.value})}
              required
              title={(() => {
                const selectedType = creativeTypes.find(t => t.code === formData.form);
                return selectedType?.description || selectedType?.name || '';
              })()}
            >
              <option value="">Выберите тип</option>
              {creativeTypes.map((type) => (
                <option 
                  key={type.code} 
                  value={type.code}
                  title={type.description || type.name}
                >
                  {type.name}
                </option>
              ))}
            </select>
            {formData.form && (() => {
              const selectedType = creativeTypes.find(t => t.code === formData.form);
              if (selectedType?.description && selectedType.description !== selectedType.name) {
                return (
                  <div style={{ color: '#666', fontSize: '12px', marginTop: '5px', fontStyle: 'italic' }}>
                    💡 {selectedType.description}
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Изначальный договор - скрываем при саморекламе */}
          {!formData.ad_types?.self_promotion && (
            <div className="form-group">
              <label>Изначальный договор <span className="required">*</span></label>
              <select
                className="form-control"
                value={formData.contract_external_id}
                onChange={(e) => setFormData({...formData, contract_external_id: e.target.value})}
                required
              >
                <option value="">Выберите договор</option>
                {contracts.map((contract) => (
                  <option key={contract.external_id} value={contract.external_id}>
                    {contract.customer_name || 'Не указан'} - {contract.comment || contract.serial || 'Без названия'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Целевые ссылки перехода */}
          <div className="form-group">
            <label>Целевые ссылки перехода</label>
            <div className="array-fields">
              {formData.target_urls.map((url, index) => (
                <div key={index} className="array-field-row">
                  <input
                    type="url"
                    className="form-control"
                    value={url}
                    onChange={(e) => updateArrayItem('target_urls', index, e.target.value)}
                    placeholder="https://example.com"
                  />
                  {formData.target_urls.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeArrayItem('target_urls', index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-add"
                onClick={() => addArrayItem('target_urls')}
              >
                Добавить
              </button>
            </div>
          </div>

          {/* Файлы */}
          <div className="form-group">
            <label>Файлы</label>
            <div className="array-fields">
              {formData.media_files.map((file, index) => (
                <div key={index} className="array-field-row">
                  <input
                    type="text"
                    className="form-control"
                    value={file}
                    onChange={(e) => updateArrayItem('media_files', index, e.target.value)}
                    placeholder="ID медиафайла"
                    readOnly
                  />
                  {formData.media_files.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeArrayItem('media_files', index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <input
                type="file"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                accept="image/*,video/*"
                multiple
              />
              <button 
                type="button" 
                className="btn-add" 
                onClick={() => document.getElementById('file-upload').click()}
                disabled={loading}
              >
                {loading ? '⏳ Загрузка...' : '📁 Выбрать файлы'}
              </button>
              <span style={{ fontSize: '14px', color: '#666', alignSelf: 'center' }}>
                (можно выбрать несколько файлов)
              </span>
            </div>
          </div>

          {/* Текстовые данные креатива */}
          <div className="form-group">
            <label>Текстовые данные креатива</label>
            <div className="array-fields">
              {formData.texts.map((text, index) => (
                <div key={index} className="array-field-row">
                  <textarea
                    className="form-control"
                    value={text}
                    onChange={(e) => updateArrayItem('texts', index, e.target.value)}
                    placeholder="Введите текст"
                    rows={4}
                  />
                  {formData.texts.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeArrayItem('texts', index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-add"
                onClick={() => addArrayItem('texts')}
              >
                Добавить
              </button>
            </div>
          </div>

          {/* Дополнительно */}
          <div className="form-section">
            <h3>Дополнительно</h3>
            <div className="info-row">
              <span className="info-label">Статус</span>
              <span className="info-value">⚪ Не сохранено</span>
            </div>
            <div className="info-row">
              <span className="info-label">Внешний ID</span>
              <span className="info-value">-</span>
            </div>
          </div>

          {/* Кнопки */}
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCreativeModal;
